/**
 * Open/focus/close ephemeral, entity-less system windows (Upgrade, Feedback).
 *
 * These look and stack like every other Space window but stay local-only:
 * `ephemeral: true` skips CRDT/backend persistence so collaborators never
 * see another user's billing or feedback panel.
 *
 * When no Space window stack is mounted (Home, marketing, onboarding),
 * `openSystemWindow` returns null and the caller falls back to a lightbox.
 */

import useSpaceStore from '../stores/spaceStore';
import { useSpatialUIStore } from '../stores/spaceSpatialUIStore';
import {
  computeSmartWindowPosition,
  generateWindowId,
  materializeWindowSizeSpec,
} from '../components/Space/utils/windowUtils';
import { UpgradeAppDefinition } from '../apps/UpgradeApp/definition';
import { FeedbackAppDefinition } from '../apps/FeedbackApp/definition';
import type { WindowSizeSpec } from '../types/apps';

export const SYSTEM_WINDOW_APP_TYPES = ['upgrade', 'feedback'] as const;
export type SystemWindowAppType = (typeof SYSTEM_WINDOW_APP_TYPES)[number];

const SYSTEM_WINDOW_META: Record<SystemWindowAppType, { title: string; size: WindowSizeSpec }> = {
  upgrade: { title: 'Upgrade', size: UpgradeAppDefinition.defaultWindowSize! },
  feedback: { title: 'Send feedback', size: FeedbackAppDefinition.defaultWindowSize! },
};

export function isSystemWindowAppType(appType: string | undefined): appType is SystemWindowAppType {
  return !!appType && (SYSTEM_WINDOW_APP_TYPES as readonly string[]).includes(appType);
}

export function hasSpaceWindowStack(): boolean {
  const state = useSpaceStore.getState();
  return state.pageType === 'space' && !!state.activeSpace?.id;
}

export function findSystemWindow(appType: SystemWindowAppType): string | undefined {
  const windows = useSpaceStore.getState().windows || {};
  const entry = Object.entries(windows).find(([, w]) => w?.appType === appType);
  return entry?.[0];
}

function revealSystemWindow(windowId: string): void {
  const spatialUI = useSpatialUIStore.getState();
  if (spatialUI.minimizedWindowIds.includes(windowId)) {
    spatialUI.restoreWindow(windowId);
  }
  useSpaceStore.getState().bringWindowToFront(windowId);
  // Pan into view at the current zoom. `fit-window` without preserveZoom
  // scales the camera until the window fills the viewport — a compact
  // feedback/upgrade pane would look like a full-screen dialog.
  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent('river:spatial-canvas-fit-window', {
        detail: { windowId, preserveZoom: true },
      }),
    );
  }, 50);
}

/**
 * Focus an existing system window of `appType`, or create a new ephemeral one.
 * Returns the window id, or null when no Space is mounted.
 */
export function openSystemWindow(appType: SystemWindowAppType): string | null {
  if (!hasSpaceWindowStack()) return null;

  const existingId = findSystemWindow(appType);
  if (existingId) {
    revealSystemWindow(existingId);
    return existingId;
  }

  const store = useSpaceStore.getState();
  const windowId = generateWindowId();
  const meta = SYSTEM_WINDOW_META[appType];
  const size = materializeWindowSizeSpec(meta.size);
  const position = computeSmartWindowPosition(size);

  store.addWindow({
    id: windowId,
    type: 'window',
    appType,
    title: meta.title,
    position,
    size,
    ephemeral: true,
  });
  store.markWindowAsLoaded(windowId);
  revealSystemWindow(windowId);
  return windowId;
}

export function closeSystemWindow(appType: SystemWindowAppType): void {
  const windowId = findSystemWindow(appType);
  if (!windowId) return;
  const actions = useSpaceStore.getState().actions;
  if (actions?.closeWindow) {
    void actions.closeWindow(windowId);
    return;
  }
  useSpaceStore.getState().removeWindow(windowId);
}
